import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function DSCRCalculator() {
  const [monthlyRent, setMonthlyRent] = useState('3000');
  const [monthlyExpenses, setMonthlyExpenses] = useState('2000');
  const [isCalculating, setIsCalculating] = useState(false);

  const calculateDSCR = () => {
    const rent = parseFloat(monthlyRent) || 0;
    const expenses = parseFloat(monthlyExpenses) || 0;
    if (expenses === 0) return 0;
    return (rent / expenses).toFixed(2);
  };

  const handleInputChange = (setter: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 300);
  };

  const dscr = calculateDSCR();
  const dscrNumber = parseFloat(dscr);

  const getQualificationStatus = () => {
    if (dscrNumber >= 1.25) return { 
      text: 'Excellent - Highly Likely to Qualify', 
      color: 'text-amber-600', 
      bg: 'bg-amber-500/10', 
      border: 'border-amber-500/30',
      icon: 'ri-star-fill'
    };
    if (dscrNumber >= 1.0) return { 
      text: 'Good - Likely to Qualify', 
      color: 'text-blue-600', 
      bg: 'bg-blue-500/10', 
      border: 'border-blue-500/30',
      icon: 'ri-checkbox-circle-fill'
    };
    if (dscrNumber >= 0.75) return { 
      text: 'Fair - May Qualify with Conditions', 
      color: 'text-slate-600', 
      bg: 'bg-slate-500/10', 
      border: 'border-slate-500/30',
      icon: 'ri-information-fill'
    };
    return { 
      text: 'Below Minimum - Unlikely to Qualify', 
      color: 'text-red-600', 
      bg: 'bg-red-500/10', 
      border: 'border-red-500/30',
      icon: 'ri-error-warning-fill'
    };
  };

  const status = getQualificationStatus();

  return (
    <section id="calculator" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            DSCR Calculator
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Calculate your Debt Service Coverage Ratio instantly. 
            See if your investment property qualifies for a DSCR loan.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
              <h3 className="text-2xl font-bold mb-2">Calculate Your DSCR</h3>
              <p className="text-blue-100">Enter your property's monthly income and expenses</p>
            </div>

            <div className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Monthly Rent Input */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <label className="block text-sm font-bold text-slate-700 mb-3">
                    <i className="ri-money-dollar-circle-line text-blue-600 mr-2"></i>
                    Monthly Rental Income
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 font-semibold">$</span>
                    <input
                      type="number"
                      value={monthlyRent}
                      onChange={handleInputChange(setMonthlyRent)}
                      className="w-full pl-8 pr-4 py-4 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all text-lg font-semibold hover:border-blue-300"
                      placeholder="3000"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-2">Total monthly rent collected</p>
                </motion.div>

                {/* Monthly Expenses Input */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <label className="block text-sm font-bold text-slate-700 mb-3">
                    <i className="ri-file-list-3-line text-blue-600 mr-2"></i>
                    Monthly Debt Payment (PITIA)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 font-semibold">$</span>
                    <input
                      type="number"
                      value={monthlyExpenses}
                      onChange={handleInputChange(setMonthlyExpenses)}
                      className="w-full pl-8 pr-4 py-4 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all text-lg font-semibold hover:border-blue-300"
                      placeholder="2000"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-2">Principal, Interest, Taxes, Insurance, HOA</p>
                </motion.div>
              </div>

              {/* DSCR Result */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={dscr}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className={`${status.bg} ${status.border} border-2 rounded-2xl p-8 mb-8 relative overflow-hidden`}
                >
                  {isCalculating && (
                    <motion.div
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 0.3 }}
                      className="absolute top-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-amber-500"
                    />
                  )}
                  
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-600 mb-2">Your DSCR Ratio</p>
                    <motion.div
                      key={dscr}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                      className="text-6xl font-bold text-slate-900 mb-4"
                    >
                      {dscr}
                    </motion.div>
                    <motion.div
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className={`inline-flex items-center px-6 py-3 ${status.bg} rounded-full ${status.border} border`}
                    >
                      <i className={`${status.icon} text-2xl ${status.color} mr-2`}></i>
                      <span className={`font-bold text-lg ${status.color}`}>{status.text}</span>
                    </motion.div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Info Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {[
                  { range: '> 1.25', label: 'Excellent DSCR', detail: 'Best rates available', color: 'amber' },
                  { range: '1.0 - 1.25', label: 'Good DSCR', detail: 'Competitive rates', color: 'blue' },
                  { range: '0.75 - 1.0', label: 'Fair DSCR', detail: 'May need larger down payment', color: 'slate' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5, scale: 1.05 }}
                    className={`bg-${item.color}-50 rounded-xl p-6 border border-${item.color}-200 hover:border-${item.color}-400 transition-all cursor-default`}
                  >
                    <div className={`text-3xl font-bold text-${item.color}-600 mb-2`}>{item.range}</div>
                    <div className="text-sm font-semibold text-slate-700">{item.label}</div>
                    <div className="text-xs text-slate-600 mt-1">{item.detail}</div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-center"
              >
                <motion.a
                  href="/apply"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 rounded-lg font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-xl hover:shadow-amber-500/50 whitespace-nowrap relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center">
                    Get Pre-Qualified Now
                    <motion.i
                      className="ri-arrow-right-line ml-2"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                    ></motion.i>
                  </span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-500"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "0%" }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.a>
                <p className="text-sm text-slate-500 mt-4">
                  <i className="ri-time-line mr-1"></i>
                  Takes less than 3 minutes • No credit check required
                </p>
              </motion.div>
            </div>
          </div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="mt-12 bg-blue-50 rounded-2xl p-8 border border-blue-200"
          >
            <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
              <i className="ri-information-line text-blue-600 mr-2"></i>
              What is DSCR?
            </h4>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Debt Service Coverage Ratio (DSCR)</strong> measures your property's ability to cover its debt payments. 
              It's calculated by dividing the property's monthly rental income by its monthly debt obligations (PITIA).
            </p>
            <p className="text-slate-700 leading-relaxed">
              Most lenders require a minimum DSCR of <strong>1.0</strong> (property income equals debt payment), 
              but a DSCR of <strong>1.25 or higher</strong> typically qualifies for the best rates and terms.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
